import { useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { CallApiSignIn, CallApiGetUserHistories } from "../Api/Enforcement";
import {
  handleSetProfile,
  handleSetUserName,
  handleSetUserPass,
  handleGetUserPass,
  handleGetProfile,
  handleIsClientAuthenticated,
  handleGetReturnTo,
  handleRemoveReturnTo,
  CLIENT_AUTH_CHANGED_EVENT,
  notifyClientAuthChanged,
} from "../Share/Func/Storage";
import { useNavigate } from "react-router-dom";
import { ShowCustomErrorModal } from "../Components/Atoms/CustomErrorModal";
import useReCaptcha from "./useReCaptcha";
import { login } from "../Api/SubUrl";

const ERROR_MESSAGES = {
  NETWORK_ERROR: "Thực hiện chưa thành công!\n Liên hệ để được hỗ trợ",
  RECAPTCHA_INVALID: "Đã xảy ra lỗi : \nreCAPTCHA không hợp lệ!",
  GENERIC_ERROR: "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
};

export default function useSignInPage() {
  const [user, setUser] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [totalBonus, setTotalBonus] = useState(0);
  const [voucherHistories, setVoucherHistories] = useState([]);
  const [introducedHistories, setIntroducedHistories] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const introduceId =  "";

  // Sử dụng hook reCAPTCHA
  const {
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    getReCaptchaToken,
    resetReCaptcha,
    isDefaultV3,
  } = useReCaptcha("login", login);

  const signInSchemaForm = yup.object().shape({
    userName: yup.string().required("Vui lòng nhập tên tài khoản"),
    password: yup.string().required("Vui lòng nhập mật khẩu"),
  });

  const { control, handleSubmit } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: useMemo(
      () => ({
        userName: "",
        password: "",
      }),
      []
    ),
    resolver: yupResolver(signInSchemaForm),
  });

  const getHistories = useCallback((formData) => {
    if (formData.userName && formData.password) {
      const param = {
        SignInModel: formData,
        Page: 0,
        PageSize: 5,
        DateFrom: null,
        DateTo: null,
      };
      CallApiGetUserHistories(param, (res) => {
        if (
          res &&
          res.ListWithRefRate &&
          res.ListWithoutRefRate &&
          typeof res.TotalBonus === "number"
        ) {
          setIntroducedHistories(res.ListWithRefRate.Items);
          setVoucherHistories(res.ListWithoutRefRate.Items);
          setTotalBonus(res.TotalBonus);
          setShowProfile(true);
        } else {
          ShowCustomErrorModal({
            message: "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
            title: "Đã xảy ra lỗi",
          });
        }
      });
    }
  }, []);

  const handleSignIn = useCallback(
    async (formData) => {
      if (loading) return;

      try {
        const token = await getReCaptchaToken();
        setLoading(true);

        // Gọi API đăng nhập với token reCAPTCHA
        CallApiSignIn(token, formData, (res) => {
          setLoading(false);
          resetReCaptcha();
          if (res.Status) {
            const status = res.Status.status;
            const message = res.Status.data.Message;
            if (status === 400)
              ShowCustomErrorModal({
                message: `<p>${message}</p>`,
                title: "Đăng nhập thất bại",
              });
            else if (status === 403)
              ShowCustomErrorModal({
                message: ERROR_MESSAGES.RECAPTCHA_INVALID,
                title: "Xác thực thất bại",
              });
            else
              ShowCustomErrorModal({
                message: ERROR_MESSAGES.GENERIC_ERROR,
                title: "Đã xảy ra lỗi",
              });
          } else {
            if (!res.Name) {
              ShowCustomErrorModal({
                message: ERROR_MESSAGES.NETWORK_ERROR,
                title: "Đã xảy ra lỗi",
              });
              return;
            }

            setUser(res);
            getHistories(formData);
            handleSetProfile(res);
            handleSetUserName(formData.userName);
            handleSetUserPass(formData.password);
            notifyClientAuthChanged();

           const returnToFromStorage = handleGetReturnTo();
           if (returnToFromStorage && returnToFromStorage !== login) {
             handleRemoveReturnTo();
             setShowProfile(false);
             navigate(returnToFromStorage, { replace: true });
             return;
           }

            setShowProfile(true);
          }
        });
      } catch (error) {
        ShowCustomErrorModal({
          message: error.message || ERROR_MESSAGES.NETWORK_ERROR,
          title: "Đăng nhập thất bại",
        });
      }
    },
    [loading, getReCaptchaToken, resetReCaptcha, getHistories, navigate]
  );

  const handleClose = useCallback(() => {
    setShowProfile(false);
    setUser(null);
  }, []);

  const handleInvalid = useCallback((errors) => {
    const errorMessages = Object.values(errors)
      .map((err) => `<p>${err.message}</p>`)
      .join("");
    ShowCustomErrorModal({
      message: errorMessages,
      title: "Đăng nhập thất bại",
    });
  }, []);

  useEffect(() => {
    const syncFromAuth = () => {
      if (handleIsClientAuthenticated()) return;
      setShowProfile(false);
      setUser(null);
      setIntroducedHistories([]);
      setVoucherHistories([]);
      setTotalBonus(0);
    };

    // initial sync (in case this hook mounts after a logout)
    syncFromAuth();

    window.addEventListener(CLIENT_AUTH_CHANGED_EVENT, syncFromAuth);
    return () =>
      window.removeEventListener(CLIENT_AUTH_CHANGED_EVENT, syncFromAuth);
  }, []);


  useEffect(() => {

    if (!handleIsClientAuthenticated()) return;

    const storedProfile = handleGetProfile();
    if (!storedProfile?.Name) return;

    setUser(storedProfile);
    setShowProfile(true);

    const storedUserName = storedProfile.Name;
    const storedPassword = handleGetUserPass();

    getHistories({
      userName: storedUserName,
      password: storedPassword,
    });
  }, [getHistories]);

  return {
    showProfile,
    user,
    control,
    handleSubmit: handleSubmit(handleSignIn, handleInvalid),
    handleClose,
    voucherHistories,
    introducedHistories,
    introduceId,
    totalBonus,
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    isDefaultV3,
    loading,
  };
}
